import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Send,
  Clock,
  CheckCircle,
  XCircle,
  Calendar,
  Facebook,
  Instagram,
  Linkedin,
  Play,
  ExternalLink,
  Filter,
  Trash2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = process.env.REACT_APP_BACKEND_URL;

const NetworkIcon = ({ network, className }) => {
  const icons = {
    facebook: Facebook,
    instagram: Instagram,
    linkedin: Linkedin
  };
  const Icon = icons[network];
  return Icon ? <Icon className={className} /> : null;
};

const NetworkBadge = ({ network }) => {
  const colors = {
    facebook: "bg-blue-500/10 text-blue-600 border-blue-500/20",
    instagram: "bg-pink-500/10 text-pink-600 border-pink-500/20",
    linkedin: "bg-sky-500/10 text-sky-600 border-sky-500/20"
  };
  
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium border ${colors[network]}`}>
      <NetworkIcon network={network} className="w-3 h-3" />
      {network.charAt(0).toUpperCase() + network.slice(1)}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const statusMap = {
    draft: { label: "Brouillon", class: "bg-gray-500/10 text-gray-600", icon: Clock },
    pending_validation: { label: "En attente", class: "bg-yellow-500/10 text-yellow-600", icon: Clock },
    approved: { label: "Approuvé", class: "bg-green-500/10 text-green-600", icon: CheckCircle },
    published: { label: "Publié", class: "bg-purple-500/10 text-purple-600", icon: CheckCircle },
    rejected: { label: "Rejeté", class: "bg-red-500/10 text-red-600", icon: XCircle },
    scheduled: { label: "Planifié", class: "bg-blue-500/10 text-blue-600", icon: Calendar },
    failed: { label: "Échec", class: "bg-red-500/10 text-red-600", icon: XCircle }
  };
  
  const { label, class: className, icon: Icon } = statusMap[status] || statusMap.draft;
  
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

export default function Publications() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [networkFilter, setNetworkFilter] = useState("all");
  const [scheduleDialog, setScheduleDialog] = useState(null);
  const [scheduleDate, setScheduleDate] = useState("");
  const [processing, setProcessing] = useState({});
  const [deleting, setDeleting] = useState({});

  useEffect(() => {
    fetchPosts();
  }, [filter, networkFilter]);

  const fetchPosts = async () => {
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      if (networkFilter !== "all") params.network = networkFilter;
      
      const response = await axios.get(`${API}/api/posts`, { params });
      setPosts(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des publications");
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (post) => {
    setProcessing(prev => ({ ...prev, [post.id]: true }));
    try {
      await axios.post(`${API}/api/${post.network}/publish`, {
        post_id: post.id
      });
      toast.success(`Publication réussie sur ${post.network}`);
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la publication");
    } finally {
      setProcessing(prev => ({ ...prev, [post.id]: false }));
    }
  };

  const handleSchedule = async () => {
    if (!scheduleDialog || !scheduleDate) return;
    
    setProcessing(prev => ({ ...prev, [scheduleDialog.id]: true }));
    try {
      await axios.patch(`${API}/api/posts/${scheduleDialog.id}`, {
        scheduled_date: scheduleDate,
        status: "scheduled"
      });
      toast.success("Publication planifiée");
      setScheduleDialog(null);
      setScheduleDate("");
      fetchPosts();
    } catch (error) {
      toast.error("Erreur lors de la planification");
    } finally {
      setProcessing(prev => ({ ...prev, [scheduleDialog.id]: false }));
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette publication ?")) {
      return;
    }
    
    setDeleting(prev => ({ ...prev, [postId]: true }));
    try {
      await axios.delete(`${API}/api/posts/${postId}`);
      toast.success("Publication supprimée");
      fetchPosts();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleting(prev => ({ ...prev, [postId]: false }));
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer TOUTES les publications ? Cette action est irréversible.")) {
      return;
    }
    
    setLoading(true);
    try {
      await axios.delete(`${API}/api/posts/all`);
      toast.success("Toutes les publications ont été supprimées");
      fetchPosts();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
      setLoading(false);
    }
  };

  const filteredPosts = posts;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Publications</h1>
          <p className="text-muted-foreground mt-1">
            Gérez et publiez votre contenu sur les réseaux sociaux
          </p>
        </div>
        <Button 
          variant="destructive" 
          onClick={handleDeleteAll}
          disabled={posts.length === 0}
          data-testid="delete-all-btn"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Tout supprimer
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filtres:</span>
            </div>
            
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="draft">Brouillon</SelectItem>
                <SelectItem value="pending_validation">En attente</SelectItem>
                <SelectItem value="approved">Approuvé</SelectItem>
                <SelectItem value="scheduled">Planifié</SelectItem>
                <SelectItem value="published">Publié</SelectItem>
              </SelectContent>
            </Select>

            <Select value={networkFilter} onValueChange={setNetworkFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Réseau" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les réseaux</SelectItem>
                <SelectItem value="facebook">Facebook</SelectItem>
                <SelectItem value="instagram">Instagram</SelectItem>
                <SelectItem value="linkedin">LinkedIn</SelectItem>
              </SelectContent>
            </Select>

            <Badge variant="secondary" className="ml-auto">
              {filteredPosts.length} publication(s)
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Posts List */}
      {filteredPosts.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground">Aucune publication trouvée</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredPosts.map((post) => (
            <Card key={post.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex gap-6">
                  {/* Image Preview */}
                  {(post.image_base64 || post.image_url) && (
                    <div className="w-32 h-32 flex-shrink-0 rounded-lg overflow-hidden bg-muted">
                      <img 
                        src={post.image_base64 ? `data:image/png;base64,${post.image_base64}` : post.image_url}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <NetworkBadge network={post.network} />
                        <StatusBadge status={post.status} />
                        {post.theme && (
                          <Badge variant="outline">{post.theme}</Badge>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(post.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    
                    <p className="text-sm text-foreground line-clamp-3 mb-4">
                      {post.content}
                    </p>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {post.status === "approved" && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handlePublish(post)}
                            disabled={processing[post.id]}
                          >
                            {processing[post.id] ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                            ) : (
                              <Send className="w-4 h-4 mr-2" />
                            )}
                            Publier
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setScheduleDialog(post)}
                          >
                            <Calendar className="w-4 h-4 mr-2" />
                            Planifier
                          </Button>
                        </>
                      )}
                      
                      {post.status === "published" && post.external_post_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(`https://facebook.com/${post.external_post_id}`, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Voir sur {post.network}
                        </Button>
                      )}

                      {post.status === "scheduled" && post.scheduled_date && (
                        <Badge variant="secondary">
                          <Calendar className="w-3 h-3 mr-1" />
                          {new Date(post.scheduled_date).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Badge>
                      )}

                      {/* Delete Button */}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10 ml-auto"
                        onClick={() => handleDelete(post.id)}
                        disabled={deleting[post.id]}
                        data-testid={`delete-post-${post.id}`}
                      >
                        {deleting[post.id] ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-destructive" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Schedule Dialog */}
      <Dialog open={!!scheduleDialog} onOpenChange={() => setScheduleDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Planifier la publication</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Date et heure de publication</Label>
              <Input
                type="datetime-local"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDialog(null)}>
              Annuler
            </Button>
            <Button onClick={handleSchedule} disabled={!scheduleDate}>
              Planifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
